import { useNavigate, useParams } from 'react-router-dom';
import FileUploadService from '../../../../../shared/services/file-upload/file-upload.service';
import { useContext, useEffect, useMemo, useState } from 'react';
import { UserNotificationsContext } from '../../../../../context/user-notifications/user-notifications';
import { createCompositeSelection, createHeaderSelection, isCompositeSelection, isConstantSelection, isHeaderSelection, MappingSelection } from '../../../../../shared/utils/mapping-selection.utils';
import { PageOuterContainer } from '../../../../../root-components/layouts/page-outer-container';
import { Alert, Box, Grid, Stack, Typography } from '@mui/material';
import { DataImportMappingTile } from '../../../../presentation-components/settings-components/data-import/data-import-mapping-tile';
import { DialogSaveButtons } from '../../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import { LoadingPage } from '../../../loading-page';
import { PageHeading } from '../../../../presentation-components/display-elements-components/page-heading';
import { IconName } from '../../../../../shared/interfaces/icon-picker.model';

export const DataImportMappingPage = () => {
  const navigate = useNavigate();
  const { id, uploadId } = useParams();
  //Services
  const fileUploadService = new FileUploadService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  //State
  const [gaiaFields, setGaiaFields] = useState<string[]>([]);
  const [fileUploadMapped, setFileUploadMapped] = useState<any>(null)
  const [headings, setHeadings] = useState<string[]>([]);
  const [mapping, setMapping] = useState<{ [gaiaField: string]: MappingSelection[] }>({})
  const [error, setError] = useState<string | null>(null)
  //Loading State
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);


  const hasMappedSelection = (selections?: MappingSelection[]) => {
    if (!selections || selections.length === 0) return false
    return selections.some(selection => {
      if (isHeaderSelection(selection)) return selection.value.trim() !== ''
      if (isConstantSelection(selection)) return Boolean((selection.displayValue ?? selection.value)?.toString().trim())
      if (isCompositeSelection(selection)) return true
      return false
    })
  }

  const mandatoryFields = useMemo<string[]>(() => {
    const opts = (fileUploadMapped?.options || []) as Array<any>
    return opts.filter(o => String(o.status).toLowerCase() === 'mandatory').map(o => o.name)
  }, [fileUploadMapped])
  const missingMandatory = useMemo<string[]>(() => {
    return mandatoryFields.filter(field => !hasMappedSelection(mapping[field]))
  }, [mandatoryFields, mapping])

  useEffect(() => {
    setIsLoading(true)
    setError(null)
    if (!uploadId) return
    fileUploadService.getUploadFileForMapping(uploadId)
      .then((response: any) => {
        if (response.code !== undefined) {
          setError(response.message || 'An error occurred while loading the mapping data')
          setFileUploadMapped(null)
          setHeadings([])
          setGaiaFields([])
          setMapping({})
          return
        }
        setFileUploadMapped(response)
        setHeadings(response.headers || [])
        setGaiaFields((response.options || []).map((o: any) => o.name))
        if (response.contactSupplier?.mappings?.length > 0) {
          const formatted: { [gaiaField: string]: MappingSelection[] } = {}
          response.contactSupplier.mappings.forEach((m: { customHeading: string; mappedHeading: string }) => {
            if (!formatted[m.mappedHeading]) formatted[m.mappedHeading] = []
            formatted[m.mappedHeading].push(createHeaderSelection(m.customHeading))
          })
          setMapping(formatted)
        }
      })
      .catch((err: any) => {
        setError(err?.response?.data?.message || err?.message || 'An error occurred while loading the mapping data')
        setFileUploadMapped(null)
        setHeadings([])
        setGaiaFields([])
        setMapping({})
      })
      .finally(() => setIsLoading(false))
  }, [uploadId])

  const getCompositeChildren = (gaiaField: string): string[] => {
    const meta = (fileUploadMapped?.options || []).find((o: any) => o.name === gaiaField)
    if (!meta) return []
    if (String(meta.fieldType).toLowerCase() !== 'composite') return []
    const struct = meta.fieldStructure
    if (Array.isArray(struct)) {
      return struct.map((s: any) => String(s).trim()).filter(Boolean)
    }
    if (typeof struct === 'string') {
      return struct.split(',').map((s: string) => s.trim()).filter(Boolean)
    }
    if (struct != null) {
      try {
        const parsed = JSON.parse(String(struct))
        if (Array.isArray(parsed)) {
          return parsed.map((s: any) => String(s).trim()).filter(Boolean)
        }
      } catch {
        return []
      }
    }
    return []
  }

  const handleMappingChange = (gaiaField: string, selections: MappingSelection[] = []) => {
    const arr = Array.isArray(selections) ? selections : []
    const compositeChildren = getCompositeChildren(gaiaField)
    setMapping(prev => {
      const next = { ...prev }
      if (!arr.length) {
        delete next[gaiaField]
      } else {
        next[gaiaField] = arr
      }
      if (compositeChildren.length > 0) {
        if (!arr.length) {
          compositeChildren.forEach(child => {
            const current = next[child]
            if (!current) return
            const filtered = current.filter(selection => !(isCompositeSelection(selection) && selection.parentField === gaiaField))
            if (!filtered.length) {
              delete next[child]
            } else {
              next[child] = filtered
            }
          })
        } else {
          compositeChildren.forEach(child => {
            next[child] = [createCompositeSelection(gaiaField)]
          })
        }
      }
      return next
    })
  }

  const handleSubmit = () => {
    if (missingMandatory.length > 0) return
    setIsSubmitting(true);
    const mappingArray: { customHeading: string; mappedHeading: string; isGlobalValue?: boolean }[] = []
    for (const gaiaField in mapping) {
      const selections = mapping[gaiaField] || []
      selections.forEach(selection => {
        if (isCompositeSelection(selection)) return
        if (isConstantSelection(selection)) {
          const customHeading = selection.value?.toString()
          if (!customHeading) return
          mappingArray.push({ customHeading, mappedHeading: gaiaField, isGlobalValue: true })
          return
        }
        if (isHeaderSelection(selection)) {
          mappingArray.push({ customHeading: selection.value, mappedHeading: gaiaField })
        }
      })
    }
    fileUploadService.uploadFileMappings({ fileUploadId: uploadId!, mappings: mappingArray })
      .then(() => {
        setIsSubmitting(false);
        userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' });
        navigate(`/settings/data-upload/import-history/${id}/${uploadId}`);
      })
      .catch(() => {
        setIsSubmitting(false);
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
      });
  };

  const deleteFileHandler = () => {
    userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: 'Are you sure you want to delete this file? This action cannot be undone.', buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
      .then(response => {
        if (response) {
          fileUploadService.deleteUpload(uploadId!, false)
            .then(() => {
              userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
              navigate(`/settings/data-upload/import-history`)
            })
            .catch(err => {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
              console.log(err)
            })
        }
      })
  }


  return (
    <PageOuterContainer>
      <PageHeading
        title='Map columns'
        includeBackButton={true}
        backButtonLink={`/settings/data-upload/import-history`}
        backButtonText='Back to import history'
        includeActionButton={true}
        actionButtonIcon={IconName.DeleteOutline}
        actionButtonHandler={deleteFileHandler}
        actionButtonText='Delete'
      />
      <LoadingPage pageReady={!isLoading}>
        <Box>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          {!error && (
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', textAlign: 'left' }}>
              Match columns from your spreadsheet to system fields.
            </Typography>
          )}
          {fileUploadMapped?.contactSupplier?.name && fileUploadMapped.contactSupplier.mappings.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Using mappings for Supplier: <strong>{fileUploadMapped.contactSupplier.name}</strong>
              </Typography>
            </Box>
          )}
          {fileUploadMapped && !error &&
            <>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Required fields</Typography>
              <Grid container spacing={4} sx={{ mb: 4 }}>
                {gaiaFields
                  .filter((f) => (fileUploadMapped.options || []).find((o: any) => o.name === f)?.status === 'mandatory')
                  .map((gaiaField: string, index: number) => (
                    <Grid item lg={4} md={6} sm={12} xs={12} key={`req-${index}`}>
                      <DataImportMappingTile
                        value={mapping[gaiaField] || []}
                        selectedValues={mapping}
                        header={gaiaField}
                        fileUpload={fileUploadMapped}
                        handleChange={handleMappingChange}
                        optionsOverride={headings.map(h => ({ name: h, label: 'header', status: 'optional', fieldType: 'header', fieldStructure: '', allowMultiple: true }))}
                        multiple={false}
                      />
                    </Grid>
                  ))}
              </Grid>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>Optional fields</Typography>
              <Grid container spacing={4}>
                {gaiaFields
                  .filter((f) => (fileUploadMapped.options || []).find((o: any) => o.name === f)?.status !== 'mandatory')
                  .map((gaiaField: string, index: number) => (
                    <Grid item lg={4} md={6} sm={12} xs={12} key={`opt-${index}`}>
                      <DataImportMappingTile
                        value={mapping[gaiaField] || []}
                        selectedValues={mapping}
                        header={gaiaField}
                        fileUpload={fileUploadMapped}
                        handleChange={handleMappingChange}
                        optionsOverride={headings.map(h => ({ name: h, label: 'header', status: 'optional', fieldType: 'header', fieldStructure: '', allowMultiple: true }))}
                        multiple={false}
                      />
                    </Grid>
                  ))}
              </Grid>
            </>}
          <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={() => navigate(`/settings/data-upload/import-history`)}
              submitHandler={handleSubmit}
            />
          </Stack>
        </Box>
      </LoadingPage>
    </PageOuterContainer>
  );
}

